import React, {useEffect, useState} from 'react'
import Swal from 'sweetalert2'
import {
  IContratoIndicador,
  IContratoMemoriaCalculo,
  IContratoSecao,
  IContratoTabela,
  ILaudoCapitulo,
} from '../interfaces/ILaudoAuditoria'
import {LaudoAuditoriaService} from '../services/LaudoAuditoriaService'
import {estadoRevisao} from '../utils/laudoRevisao'

interface Props {
  capitulo: ILaudoCapitulo
  onRecarregar: () => Promise<void>
  // Chamado depois de aceitar ou recusar — a tela recarrega e pula para o próximo pendente.
  onRevisado: (capituloId: number) => Promise<void>
}

// DEVN3-521 / AUD-REL-001 — detalhe de um capítulo na tela /auditoria/revisao. Bloco sistêmico
// (só leitura: contexto, indicadores, tabelas/seções, memória de cálculo, evidências) + a redação
// da IA, que o revisor pode editar, regenerar, tornar mais objetiva/técnica, salvar como rascunho,
// aceitar (entra no PDF) ou recusar (fica fora). A carga/polling é do AuditoriaRevisaoPage.
const AuditoriaLaudoPainel: React.FC<Props> = ({capitulo, onRecarregar, onRevisado}) => {
  const [acao, setAcao] = useState<string | null>(null)
  const [editando, setEditando] = useState(false)
  const [verSugestao, setVerSugestao] = useState(false)
  const [abertura, setAbertura] = useState('')
  const [leitura, setLeitura] = useState('')
  const [fechamento, setFechamento] = useState('')

  useEffect(() => {
    setAbertura(capitulo?.textoAbertura ?? '')
    setLeitura(capitulo?.textoLeituraTecnica ?? '')
    setFechamento(capitulo?.textoFechamento ?? '')
    setEditando(false)
    setVerSugestao(false)
  }, [
    capitulo.id,
    capitulo.dataGeracao,
    capitulo.textoAbertura,
    capitulo.textoLeituraTecnica,
    capitulo.textoFechamento,
  ])

  if (capitulo.statusCapitulo === 'PENDENTE') {
    return (
      <div className='auditoria-card'>
        <DisclaimerLaudo />
        <div className='d-flex align-items-center gap-2 mt-3'>
          <span className='spinner-border spinner-border-sm' />
          <span>Gerando o texto deste capítulo…</span>
        </div>
      </div>
    )
  }

  const contrato = capitulo.contrato
  const insuficiente = capitulo.statusCapitulo === 'ENTRADA_INSUFICIENTE'
  const comErro = capitulo.statusCapitulo === 'ERRO'
  const emAcao = acao !== null
  const sugestao = capitulo.sugestaoIa
  const estado = estadoRevisao(capitulo)

  const rodarAcao = async (nome: string, fn: () => Promise<unknown>, msgErro: string) => {
    setAcao(nome)
    try {
      await fn()
      await onRecarregar()
    } catch (e: any) {
      Swal.fire('Erro', e?.response?.data?.mensagem ?? msgErro, 'error')
    } finally {
      setAcao(null)
    }
  }

  const concluirRevisao = async (nome: string, fn: () => Promise<unknown>, msgErro: string) => {
    setAcao(nome)
    try {
      await fn()
      await onRevisado(capitulo.id)
    } catch (e: any) {
      Swal.fire('Erro', e?.response?.data?.mensagem ?? msgErro, 'error')
    } finally {
      setAcao(null)
    }
  }

  const aceitar = () =>
    concluirRevisao(
      'aceitar',
      () => LaudoAuditoriaService.aprovar(capitulo.id),
      'Não foi possível aceitar o capítulo.'
    )

  const recusar = () =>
    concluirRevisao(
      'recusar',
      () => LaudoAuditoriaService.rejeitar(capitulo.id),
      'Não foi possível recusar o capítulo.'
    )

  const regenerar = async () => {
    const {value: refinamento, isConfirmed} = await Swal.fire({
      title: 'Regenerar capítulo',
      input: 'textarea',
      inputLabel: 'Instrução opcional para a IA (a mesma base de dados é mantida)',
      inputPlaceholder: 'ex.: enfatizar a divergência do 1º trimestre; ser mais conciso na abertura…',
      showCancelButton: true,
      confirmButtonText: 'Regenerar',
      cancelButtonText: 'Cancelar',
    })
    if (!isConfirmed) return
    await rodarAcao(
      'regenerar',
      () => LaudoAuditoriaService.regenerar(capitulo.id, {refinamento: refinamento || undefined}),
      'Não foi possível regenerar o capítulo.'
    )
  }

  const ajustarEstilo = (modo: 'MAIS_OBJETIVO' | 'MAIS_TECNICO') =>
    rodarAcao(
      modo,
      () => LaudoAuditoriaService.regenerar(capitulo.id, {modo}),
      'Não foi possível ajustar o texto.'
    )

  const alterado =
    abertura !== (capitulo.textoAbertura ?? '') ||
    leitura !== (capitulo.textoLeituraTecnica ?? '') ||
    fechamento !== (capitulo.textoFechamento ?? '')

  const salvar = (rascunho: boolean) =>
    rodarAcao(
      rascunho ? 'rascunho' : 'salvar',
      async () => {
        const payload = {abertura, leituraTecnica: leitura, fechamento}
        rascunho
          ? await LaudoAuditoriaService.salvarRascunho(capitulo.id, payload)
          : await LaudoAuditoriaService.editarCapitulo(capitulo.id, payload)
        setEditando(false)
      },
      'Não foi possível salvar.'
    )

  const desfazer = () => {
    setAbertura(capitulo.textoAbertura ?? '')
    setLeitura(capitulo.textoLeituraTecnica ?? '')
    setFechamento(capitulo.textoFechamento ?? '')
    setEditando(false)
  }

  return (
    <div className='auditoria-card'>
      <DisclaimerLaudo />

      <h3 className='mt-3'>
        Laudo IA — {capitulo.categoria}
        <span className='badge ms-2 bg-info'>Redação por IA</span>
        {estado === 'ACEITO' && <span className='badge ms-2 bg-success'>Aceito</span>}
        {estado === 'RECUSADO' && <span className='badge ms-2 bg-danger'>Recusado</span>}
      </h3>

      {comErro && (
        <div className='alert alert-danger'>
          A geração deste capítulo falhou, então ele não entra no laudo nem bloqueia a exportação.
          Use “Regenerar” para tentar de novo.
        </div>
      )}

      {insuficiente && (
        <div className='alert alert-info'>
          O motor de regras não produziu dados suficientes para este procedimento no período (por
          exemplo, regime tributário incompatível). {contrato?.mensagemDirecionamento}
          {capitulo.camposAusentes.length > 0 && (
            <ul className='mb-0 mt-2'>
              {capitulo.camposAusentes.map((c, i) => (
                <li key={i}>{c}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {!comErro && contrato && (
        <>
          <div className='auditoria-card' style={{borderLeft: '3px solid #005E96'}}>
            <p className='mb-1'>
              <span className={`auditoria-status status-${contrato.status}`}>{contrato.status}</span>{' '}
              {contrato.mensagemDirecionamento}
            </p>
            {contrato.acaoRecomendada &&
              contrato.acaoRecomendada !== contrato.mensagemDirecionamento && (
                <p className='mb-1'>
                  <strong>Ação informada pelo motor.</strong> {contrato.acaoRecomendada}
                </p>
              )}

            <ContextoBloco contrato={contrato} />

            <Indicadores itens={contrato.indicadores} fallback={contrato} />

            <details className='mt-2'>
              <summary>Objetivo, procedimento e critério</summary>
              <p className='mb-1 mt-2'>
                <strong>Objetivo.</strong> {contrato.objetivo}
              </p>
              <p className='mb-1'>
                <strong>Procedimento.</strong> {contrato.procedimento}
              </p>
              <p className='mb-0'>
                <strong>Critério.</strong> {contrato.criterio}
              </p>
            </details>
          </div>

          {contrato.secoes.length > 0 ? (
            contrato.secoes.map((s, i) => <Secao key={i} secao={s} />)
          ) : (
            contrato.tabelas.map((t, i) => <Tabela key={i} tabela={t} />)
          )}

          <Memorias itens={contrato.memoriasCalculo} titulo='Memória de cálculo' />

          {contrato.evidencias.length > 0 && (
            <div className='mt-3'>
              <h5>Evidências e referências</h5>
              <div style={{overflowX: 'auto'}}>
                <table className='auditoria-table'>
                  <thead>
                    <tr>
                      <th>Tipo</th>
                      <th>Descrição</th>
                      <th>Origem</th>
                      <th>Documento</th>
                      <th>Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contrato.evidencias.map((e, i) => (
                      <tr key={i}>
                        <td>{e.tipo}</td>
                        <td>{e.descricao}</td>
                        <td>{e.origem}</td>
                        <td>{e.documento}</td>
                        <td>{e.data}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {contrato.fundamentacaoLegal && (
            <details className='mt-3'>
              <summary>Fundamentação apresentada pela regra</summary>
              <p style={{whiteSpace: 'pre-wrap', fontSize: 13}}>{contrato.fundamentacaoLegal}</p>
            </details>
          )}
        </>
      )}

      {capitulo.warnings.length > 0 && (
        <div className='alert alert-warning mt-3'>
          <ul className='mb-0'>
            {capitulo.warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      {!comErro && (
        <>
      <div className='d-flex align-items-center justify-content-between mt-4'>
        <h5 className='mb-0'>
          Redação sugerida pela IA
          {editando && <span className='text-primary ms-2'>(editando)</span>}
        </h5>
        {sugestao && !editando && (
          <button
            className='btn btn-sm btn-link p-0'
            onClick={() => setVerSugestao((v) => !v)}
          >
            {verSugestao ? 'Ocultar texto sugerido' : 'Ver Texto Atual × Texto Sugerido'}
          </button>
        )}
      </div>

      {[
        ['Abertura', abertura, setAbertura, sugestao?.abertura] as const,
        ['Leitura técnica', leitura, setLeitura, sugestao?.leituraTecnica] as const,
        ['Fechamento', fechamento, setFechamento, sugestao?.fechamento] as const,
      ].map(([rotulo, valor, setter, textoSugerido]) => (
        <div key={rotulo} className='mb-2'>
          <label className='auditoria-label'>{rotulo}</label>
          {editando ? (
            <textarea
              className='form-control'
              rows={rotulo === 'Leitura técnica' ? 8 : 4}
              value={valor}
              onChange={(e) => setter(e.target.value)}
            />
          ) : verSugestao && textoSugerido != null ? (
            <div className='row g-2'>
              <div className='col-md-6'>
                <div className='text-muted' style={{fontSize: 11}}>
                  Texto atual
                </div>
                <p style={{whiteSpace: 'pre-wrap', margin: 0}}>{valor || '—'}</p>
              </div>
              <div className='col-md-6' style={{borderLeft: '1px solid #e3e8ef'}}>
                <div className='text-muted' style={{fontSize: 11}}>
                  Texto sugerido pela IA
                </div>
                <p style={{whiteSpace: 'pre-wrap', margin: 0, color: '#47586b'}}>
                  {textoSugerido || '—'}
                </p>
              </div>
            </div>
          ) : (
            <p style={{whiteSpace: 'pre-wrap', margin: 0}}>{valor || '—'}</p>
          )}
        </div>
      ))}
        </>
      )}

      <div className='d-flex flex-wrap gap-2 mt-3'>
        {comErro ? (
          <button className='btn btn-sm btn-primary' disabled={emAcao} onClick={regenerar}>
            {acao === 'regenerar' && <span className='spinner-border spinner-border-sm me-1' />}
            Regenerar
          </button>
        ) : editando ? (
          <>
            <button
              className='btn btn-sm btn-primary'
              disabled={emAcao || !alterado}
              onClick={() => salvar(false)}
            >
              {acao === 'salvar' && <span className='spinner-border spinner-border-sm me-1' />}
              Salvar
            </button>
            <button
              className='btn btn-sm btn-outline-primary'
              disabled={emAcao || !alterado}
              onClick={() => salvar(true)}
            >
              {acao === 'rascunho' && <span className='spinner-border spinner-border-sm me-1' />}
              Salvar rascunho
            </button>
            <button className='btn btn-sm btn-outline-secondary' disabled={emAcao} onClick={desfazer}>
              Desfazer
            </button>
          </>
        ) : (
          <>
            <button
              className='btn btn-sm btn-outline-secondary'
              disabled={emAcao}
              onClick={() => setEditando(true)}
            >
              Editar
            </button>
            <button className='btn btn-sm btn-outline-secondary' disabled={emAcao} onClick={regenerar}>
              {acao === 'regenerar' && <span className='spinner-border spinner-border-sm me-1' />}
              Regenerar
            </button>
            <button
              className='btn btn-sm btn-outline-secondary'
              disabled={emAcao}
              onClick={() => ajustarEstilo('MAIS_OBJETIVO')}
            >
              {acao === 'MAIS_OBJETIVO' && <span className='spinner-border spinner-border-sm me-1' />}
              Mais objetivo
            </button>
            <button
              className='btn btn-sm btn-outline-secondary'
              disabled={emAcao}
              onClick={() => ajustarEstilo('MAIS_TECNICO')}
            >
              {acao === 'MAIS_TECNICO' && <span className='spinner-border spinner-border-sm me-1' />}
              Mais técnico
            </button>
            <button
              className='btn btn-sm btn-outline-danger ms-auto'
              disabled={emAcao || estado === 'RECUSADO'}
              onClick={recusar}
            >
              {acao === 'recusar' && <span className='spinner-border spinner-border-sm me-1' />}
              {estado === 'RECUSADO' ? 'Recusado' : 'Recusar'}
            </button>
            <button
              className='btn btn-sm btn-primary'
              disabled={emAcao || estado === 'ACEITO'}
              onClick={aceitar}
            >
              {acao === 'aceitar' && <span className='spinner-border spinner-border-sm me-1' />}
              {estado === 'ACEITO' ? 'Aceito' : 'Aceitar'}
            </button>
          </>
        )}
      </div>

      {estado === 'ACEITO' && (
        <p className='text-success mt-2 mb-0' style={{fontSize: 13}}>
          Capítulo aceito — entra no PDF do laudo.
        </p>
      )}
      {estado === 'RECUSADO' && (
        <p className='text-danger mt-2 mb-0' style={{fontSize: 13}}>
          Capítulo recusado — fica fora do PDF do laudo.
        </p>
      )}
    </div>
  )
}

const ContextoBloco: React.FC<{contrato: NonNullable<ILaudoCapitulo['contrato']>}> = ({contrato}) => {
  const c = contrato.contexto
  const linhas: [string, string][] = []
  if (c?.tipoAuditoria) linhas.push(['Tipo de auditoria', c.tipoAuditoria])
  if (c?.regime) linhas.push(['Regime de apuração', c.regime])
  if (c?.conta) linhas.push(['Conta', c.conta])
  if (c?.contaReferencial) linhas.push(['Conta referencial', c.contaReferencial])
  if (c?.fontePrimaria) linhas.push(['Fonte primária', c.fontePrimaria])
  if (c?.fonteComparativa) linhas.push(['Fonte comparativa', c.fonteComparativa])
  if (c?.escopo) linhas.push(['Escopo', c.escopo])
  if (c?.materialidade) linhas.push(['Materialidade', c.materialidade])
  if (linhas.length === 0) return null
  return (
    <dl className='row mb-2' style={{fontSize: 13}}>
      {linhas.map(([k, v]) => (
        <React.Fragment key={k}>
          <dt className='col-sm-3 text-muted'>{k}</dt>
          <dd className='col-sm-9 mb-1'>{v}</dd>
        </React.Fragment>
      ))}
    </dl>
  )
}

const Indicadores: React.FC<{
  itens: IContratoIndicador[]
  fallback?: NonNullable<ILaudoCapitulo['contrato']>
}> = ({itens, fallback}) => {
  const lista =
    itens.length > 0
      ? itens
      : fallback
      ? [
          {rotulo: 'Impacto fiscal', valor: fallback.impactoFiscalTotal.exibicao, destaque: true},
          {rotulo: 'Impacto financeiro', valor: fallback.impactoFinanceiroTotal.exibicao, destaque: false},
        ]
      : []
  if (lista.length === 0) return null
  return (
    <div className='d-flex flex-wrap gap-2 my-2'>
      {lista.map((i, idx) => (
        <div
          key={idx}
          className='border rounded px-3 py-2'
          style={{minWidth: 130, background: i.destaque ? '#eef6fb' : undefined}}
        >
          <div className='text-muted' style={{fontSize: 10, textTransform: 'uppercase'}}>
            {i.rotulo}
          </div>
          <div style={{fontSize: 15, fontWeight: 600}}>{i.valor}</div>
        </div>
      ))}
    </div>
  )
}

const Memorias: React.FC<{itens: IContratoMemoriaCalculo[]; titulo: string}> = ({itens, titulo}) => {
  if (itens.length === 0) return null
  return (
    <div className='mt-3'>
      <h5>{titulo}</h5>
      {itens.map((m, i) => (
        <div key={i} className='border rounded p-3 mb-2'>
          <strong style={{fontSize: 13}}>{m.titulo}</strong>
          {m.formula && (
            <pre
              className='mt-1 mb-1 p-2'
              style={{background: '#f4f6f9', fontSize: 12, whiteSpace: 'pre-wrap'}}
            >
              {m.formula}
            </pre>
          )}
          {m.componentes.length > 0 && (
            <table className='mb-1' style={{fontSize: 12}}>
              <tbody>
                {m.componentes.map((c, j) => (
                  <tr key={j}>
                    <td style={{paddingRight: 16}}>{c.rotulo}</td>
                    <td style={{textAlign: 'right'}}>{c.valor}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {m.resultado && (
            <div style={{fontSize: 13}}>
              Resultado: <strong style={{color: '#044066'}}>{m.resultado}</strong>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

const Tabela: React.FC<{tabela: IContratoTabela}> = ({tabela: t}) => (
  <div className='mt-3'>
    <h5>{t.titulo}</h5>
    {t.totalLinhas === 0 ? (
      <p className='text-muted'>
        Nenhuma ocorrência identificada para os critérios deste procedimento no período.
      </p>
    ) : (
      <>
        {t.truncada && (
          <p className='text-muted' style={{fontSize: 12}}>
            Exibindo {t.linhas.length} de {t.totalLinhas} registros.
          </p>
        )}
        <div style={{overflowX: 'auto'}}>
          <table className='auditoria-table'>
            <thead>
              <tr>
                {t.colunas.map((c, j) => (
                  <th key={j}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {t.linhas.map((linha, li) => (
                <tr key={li}>
                  {linha.map((cel, ci) => (
                    <td key={ci}>{cel}</td>
                  ))}
                </tr>
              ))}
            </tbody>
            {t.totais.length > 0 && (
              <tfoot>
                {t.totais.map((tot, ti) => (
                  <tr key={ti}>
                    <td colSpan={Math.max(1, t.colunas.length - 1)}>{tot.rotulo}</td>
                    <td>{tot.valor}</td>
                  </tr>
                ))}
              </tfoot>
            )}
          </table>
        </div>
      </>
    )}
  </div>
)

const Secao: React.FC<{secao: IContratoSecao}> = ({secao: s}) => (
  <div className='mt-3'>
    <h5 className='d-flex align-items-center gap-2'>
      {s.titulo}
      <span className={`auditoria-status status-${s.status}`} style={{fontSize: 11}}>
        {s.status === 'SEM_OCORRENCIA' ? 'SEM OCORRÊNCIA' : s.status}
      </span>
    </h5>
    {s.descricao && <p className='text-muted' style={{fontSize: 13}}>{s.descricao}</p>}
    {s.indicadores.length > 0 && <Indicadores itens={s.indicadores} />}
    {s.tabelas.map((t, i) => (
      <Tabela key={i} tabela={t} />
    ))}
    <Memorias itens={s.memoriasCalculo} titulo='Memória de cálculo da seção' />
  </div>
)

const DisclaimerLaudo: React.FC = () => (
  <div className='alert alert-secondary mb-0' style={{fontSize: 13}}>
    <strong>Laudo assistido por IA.</strong> Regra decide · IA redige · Evidência suporta ·
    Profissional revisa. Os valores, contas, datas, divergências e classificações são produzidos
    pelo sistema e <strong>não são editáveis</strong>. A IA redige apenas a abertura, a leitura
    técnica e o fechamento factual, sempre submetidos à sua revisão.
  </div>
)

export default AuditoriaLaudoPainel
